import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import styled from 'styled-components';

import TextInput from 'components/common/Inputs/TextInput';

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const SliderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 35px;
    width: 100%;
    justify-content: center;

    @media (max-width: 600px) {
        flex-wrap: wrap;
        gap: 12px 16px;
        justify-content: space-between;
    }
`;

const InputWrapper = styled.div`
    width: 120px;

    input {
        text-align: center;
    }

    @media (max-width: 600px) {
        width: calc(50% - 8px);
    }
`;

const SliderWrapper = styled.div`
    position: relative;
    height: 30px;
    display: flex;
    align-items: center;
    flex: 1;
    max-width: 250px;
    touch-action: none;

    @media (max-width: 600px) {
        max-width: 100%;
        width: 100%;
        height: 44px;
        order: 3;
    }
`;

const SliderTrack = styled.div`
    position: absolute;
    width: 100%;
    height: 4px;
    background: var(--color-alt-second);
    border-radius: 2px;
`;

const SliderRange = styled.div<{ left: number; width: number }>`
    position: absolute;
    height: 4px;
    background: var(--color-alt);
    border-radius: 2px;
    left: ${(props) => props.left}%;
    width: ${(props) => props.width}%;
`;

const SliderThumb = styled.div<{ position: number }>`
    position: absolute;
    width: 16px;
    height: 16px;
    background: var(--color-white);
    border: 2px solid var(--color-alt);
    border-radius: 50%;
    cursor: pointer;
    left: ${(props) => props.position}%;
    transform: translateX(-50%);
    z-index: 2;
    transition: box-shadow 0.2s;
    touch-action: none;

    &:hover {
        box-shadow: 0 0 0 3px rgba(2, 85, 170, 0.15);
    }

    &:active {
        box-shadow: 0 0 0 4px rgba(2, 85, 170, 0.2);
    }

    @media (max-width: 768px) {
        width: 24px;
        height: 24px;
    }
`;

const ValuesWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 400px;
    margin-top: 8px;

    @media (max-width: 600px) {
        max-width: 100%;
    }
`;

const ValueLabel = styled.span`
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
`;

export interface RangeSliderProps {
    min: number;
    max: number;
    minValue: number;
    maxValue: number;
    onChange: (minValue: number, maxValue: number) => void;
    formatValue?: (value: number) => string;
    step?: number;
    suffix?: string;
    minLabel?: string;
    maxLabel?: string;
    disabled?: boolean;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
    min,
    max,
    minValue,
    maxValue,
    onChange,
    formatValue = (value) => value.toLocaleString('pl-PL'),
    step = 1,
    suffix = '',
    disabled = false
}) => {
    const sliderRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
    const [sliderMinValue, setSliderMinValue] = useState(minValue);
    const [sliderMaxValue, setSliderMaxValue] = useState(maxValue);
    const [inputMinValue, setInputMinValue] = useState<number | undefined>(minValue);
    const [inputMaxValue, setInputMaxValue] = useState<number | undefined>(maxValue);

    useEffect(() => {
        setSliderMinValue(minValue);
        setSliderMaxValue(maxValue);
        setInputMinValue(minValue);
        setInputMaxValue(maxValue);
    }, [minValue, maxValue]);

    const getPercentage = (value: number) => {
        if (max === min) return 0;

        return (value - min) / (max - min) * 100;
    };

    const getValueFromPosition = useCallback((clientX: number) => {
        if (!sliderRef.current) return min;

        const rect = sliderRef.current.getBoundingClientRect();
        const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const rawValue = min + percentage * (max - min);
        const steppedValue = Math.round(rawValue / step) * step;

        return Math.max(min, Math.min(max, steppedValue));
    }, [
        min,
        max,
        step
    ]);

    const handleMouseDown = (thumb: 'min' | 'max') => (e: React.MouseEvent) => {
        if (disabled) return;
        e.preventDefault();
        setIsDragging(thumb);
    };

    const handleTouchStart = (thumb: 'min' | 'max') => (e: React.TouchEvent) => {
        if (disabled) return;
        e.preventDefault();
        setIsDragging(thumb);
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || disabled) return;

        const newValue = getValueFromPosition(e.clientX);

        if (isDragging === 'min') {
            const clampedValue = Math.min(newValue, sliderMaxValue - step);
            setSliderMinValue(clampedValue);
            setInputMinValue(clampedValue);
        } else {
            const clampedValue = Math.max(newValue, sliderMinValue + step);
            setSliderMaxValue(clampedValue);
            setInputMaxValue(clampedValue);
        }
    }, [
        isDragging,
        sliderMinValue,
        sliderMaxValue,
        getValueFromPosition,
        step,
        disabled
    ]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (!isDragging || disabled) return;
        e.preventDefault();

        const touch = e.touches[0];
        const newValue = getValueFromPosition(touch.clientX);

        if (isDragging === 'min') {
            const clampedValue = Math.min(newValue, sliderMaxValue - step);
            setSliderMinValue(clampedValue);
            setInputMinValue(clampedValue);
        } else {
            const clampedValue = Math.max(newValue, sliderMinValue + step);
            setSliderMaxValue(clampedValue);
            setInputMaxValue(clampedValue);
        }
    }, [
        isDragging,
        sliderMinValue,
        sliderMaxValue,
        getValueFromPosition,
        step,
        disabled
    ]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            onChange(sliderMinValue, sliderMaxValue);
        }
        setIsDragging(null);
    }, [
        isDragging,
        sliderMinValue,
        sliderMaxValue,
        onChange
    ]);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', handleMouseUp);

            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
                window.removeEventListener('touchmove', handleTouchMove);
                window.removeEventListener('touchend', handleMouseUp);
            };
        }
    }, [
        isDragging,
        handleMouseMove,
        handleMouseUp,
        handleTouchMove
    ]);

    const handleMinBlur = () => {
        const valueToValidate = inputMinValue ?? sliderMinValue;
        const newMinValue = Math.max(min, Math.min(valueToValidate, sliderMaxValue - step));
        setSliderMinValue(newMinValue);
        setInputMinValue(newMinValue);
        onChange(newMinValue, sliderMaxValue);
    };

    const handleMaxBlur = () => {
        const valueToValidate = inputMaxValue ?? sliderMaxValue;
        const newMaxValue = Math.min(max, Math.max(valueToValidate, sliderMinValue + step));
        setSliderMaxValue(newMaxValue);
        setInputMaxValue(newMaxValue);
        onChange(sliderMinValue, newMaxValue);
    };

    const minPos = getPercentage(sliderMinValue);
    const maxPos = getPercentage(sliderMaxValue);

    return (
        <Container>
            <SliderRow>
                <InputWrapper>
                    <NumericFormat
                        value={inputMinValue}
                        onValueChange={(values) => {
                            setInputMinValue(values.floatValue);
                        }}
                        onBlur={handleMinBlur}
                        thousandSeparator=" "
                        customInput={TextInput}
                        disabled={disabled}
                        allowNegative={false}
                    />
                </InputWrapper>

                <SliderWrapper ref={sliderRef}>
                    <SliderTrack />
                    <SliderRange left={minPos} width={maxPos - minPos} />
                    <SliderThumb
                        position={minPos}
                        onMouseDown={handleMouseDown('min')}
                        onTouchStart={handleTouchStart('min')}
                        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                    <SliderThumb
                        position={maxPos}
                        onMouseDown={handleMouseDown('max')}
                        onTouchStart={handleTouchStart('max')}
                        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                </SliderWrapper>

                <InputWrapper>
                    <NumericFormat
                        value={inputMaxValue}
                        onValueChange={(values) => {
                            setInputMaxValue(values.floatValue);
                        }}
                        onBlur={handleMaxBlur}
                        thousandSeparator=" "
                        customInput={TextInput}
                        disabled={disabled}
                        allowNegative={false}
                    />
                </InputWrapper>
            </SliderRow>

            <ValuesWrapper>
                <ValueLabel>{formatValue(min)} {suffix}</ValueLabel>
                <ValueLabel>{formatValue(max)} {suffix}</ValueLabel>
            </ValuesWrapper>
        </Container>
    );
};

export default RangeSlider;
